/**
 * canvas-engine/item-contract/itemRenderContract.js
 *
 * SINGLE SOURCE OF TRUTH for the per-type render rules shared by the Canva-style
 * canvas builder (`admin-console-ui`) and the published-page reader (`academy-ui`,
 * via the synced `canvas-engine/` mirror). It is the structural fix for builder ↔
 * reader drift (design D1 / Requirement 3): both `ItemView` (builder) and
 * `CanvasSectionReader` (reader) become thin adapters that turn this module's DATA
 * output into React, so they cannot disagree about how an Item looks.
 *
 * Three guarantees make this module consumable by every role, including the non-React
 * Parity_Verifier (design D2):
 *   - PURE: imports only the shared constants + the shared Theme_Resolver. No React,
 *     no DOM, nothing from `modules/template-builder/` or `academy-ui/src/template/`.
 *   - TOTAL: never throws for any input (malformed geometry/content/style, unknown
 *     type, null theme) — every path resolves to a defined result.
 *   - RETURNS DATA, NOT JSX: `resolveBoxStyle` returns a plain style object and
 *     `resolveContent` returns a serializable ContentDescriptor `{ kind, props,
 *     children }`. The React adapters map that descriptor to elements.
 *
 * Every default and per-type branch below is transcribed EXACTLY from today's
 * `surface/ItemView.jsx` (via `itemConstants`) so already-saved payloads render
 * unchanged (Requirement 16.3) and the builder's look is preserved bit-for-bit.
 *
 * Requirements: 3.1, 3.4, 3.5, 6.1, 6.2, 6.3, 7.1–7.7, 8.1–8.5, 15.3, 16.5, 18.1.
 * Design: Components §3 (Render_Contract); Data Models (Item_Style table, Render_Result);
 * Properties 8 (per-type box defaults), 9 (builder/reader equality), 10 (honored
 * overrides), 11 (omitted → default), 12 (malformed → default), 13 (grid columns),
 * 18 (unknown type → empty content).
 */

import {
  MAX_GRID_COLS,
  SHADOW_PRESETS,
  BOX_DEFAULTS,
  BUTTON_FALLBACK,
  BACKGROUND_FALLBACK,
  CONTENT_DEFAULTS,
  ITEM_TYPES,
} from './itemConstants.js';
import { resolveItemStyle } from './itemThemeResolver.js';

// ── Internal primitives ──────────────────────────────────────────────────────

/** Fallback box defaults for an unknown/unsupported Item `type` (matches ItemView's
 *  default branch: radius 0, no border-width, neutral border, no padding/shadow). */
const DEFAULT_BOX = { borderRadius: 0, borderWidth: 0, borderColor: '#e5e7eb', padding: 0, boxShadow: 'none', opacity: 1 };

/** True for a real, finite JS number (never `NaN`/`Infinity`/non-number). */
function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Clamp `value` into the inclusive `[lo, hi]` range. */
function clamp(value, lo, hi) {
  return Math.min(hi, Math.max(lo, value));
}

/** The per-type box defaults for `type`, falling back to DEFAULT_BOX for unknown types. */
function boxDefaultsFor(type) {
  return BOX_DEFAULTS[type] || DEFAULT_BOX;
}

// ── Color parsing (Req 7.7, 8.4) ──────────────────────────────────────────────

/** A small set of CSS named colors accepted as "parseable" (covers the common
 *  authoring palette plus the keywords used as box defaults). */
const NAMED_COLORS = new Set([
  'transparent', 'currentcolor', 'inherit',
  'black', 'white', 'red', 'green', 'blue', 'yellow', 'orange', 'purple', 'pink',
  'gray', 'grey', 'silver', 'gold', 'brown', 'cyan', 'magenta', 'lime', 'navy',
  'teal', 'maroon', 'olive', 'aqua', 'fuchsia', 'indigo', 'violet', 'coral',
  'salmon', 'khaki', 'crimson', 'turquoise', 'beige', 'ivory', 'lavender',
]);

/**
 * Total color validator (Req 8.4): true when `value` is a string CSS color the
 * Render_Contract treats as parseable — hex (#rgb/#rgba/#rrggbb/#rrggbbaa),
 * functional `rgb()/rgba()/hsl()/hsla()`, or a known named color. A non-parseable
 * color is treated as ABSENT so the Theme_Cascade resolves it as if no override
 * existed. Never throws.
 *
 * @param {*} value  the candidate color
 * @returns {boolean} true when `value` is a parseable color string
 */
export function isParseableColor(value) {
  if (typeof value !== 'string') return false;
  const v = value.trim().toLowerCase();
  if (!v) return false;
  if (/^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/.test(v)) return true;
  if (/^rgba?\([^)]*\)$/.test(v)) return true;
  if (/^hsla?\([^)]*\)$/.test(v)) return true;
  return NAMED_COLORS.has(v);
}

// ── Style-option coercion (Req 7.7, 16.5) ─────────────────────────────────────

const TEXT_ALIGNS = new Set(['left', 'center', 'right', 'justify']);
const OBJECT_FITS = new Set(['cover', 'contain', 'fill', 'none', 'scale-down']);
const BUTTON_VARIANTS = new Set(['filled', 'outline', 'text']);
const FONT_WEIGHT_KEYWORDS = new Set(['normal', 'bold', 'lighter', 'bolder']);

/**
 * Validate and coerce ONE Item_Style option to its defined range from the design's
 * Item_Style table (Req 7.7). Returns the accepted value when it is valid/in-range,
 * otherwise `undefined` to signal "treat as absent" — the caller (`resolveBoxStyle`
 * / `resolveContent`) then substitutes the single defined per-type default for that
 * option. Colors that are not parseable likewise return `undefined` so the
 * Theme_Cascade resolves them as if absent (Req 8.4). Total: never throws.
 *
 * Ranges (design Item_Style table):
 *   fontSize 6–300 · fontWeight 100–900 or keyword · textAlign enum · lineHeight >0 ·
 *   objectFit enum · gridColumns 1–12 (integer) · gridGap 0–256 · buttonVariant enum ·
 *   borderRadius 0–9999 · borderWidth 0–100 · padding 0–9999 · opacity 0.0–1.0 ·
 *   boxShadow preset key · textColor/backgroundColor/borderColor parseable color ·
 *   fontFamily non-empty string.
 *
 * @param {string} name   the Item_Style option name
 * @param {*} value       the stored value (any type)
 * @returns {*} the coerced valid value, or `undefined` when absent/malformed/out-of-range
 */
export function coerceStyleOption(name, value) {
  switch (name) {
    case 'fontSize':
      return isFiniteNumber(value) && value >= 6 && value <= 300 ? value : undefined;
    case 'fontWeight':
      if (isFiniteNumber(value) && value >= 100 && value <= 900) return value;
      if (typeof value === 'string' && FONT_WEIGHT_KEYWORDS.has(value)) return value;
      return undefined;
    case 'textAlign':
      return typeof value === 'string' && TEXT_ALIGNS.has(value) ? value : undefined;
    case 'lineHeight':
      return isFiniteNumber(value) && value > 0 ? value : undefined;
    case 'objectFit':
      return typeof value === 'string' && OBJECT_FITS.has(value) ? value : undefined;
    case 'gridColumns':
      return isFiniteNumber(value) && value >= 1 && value <= 12 ? Math.round(value) : undefined;
    case 'gridGap':
      return isFiniteNumber(value) && value >= 0 && value <= 256 ? value : undefined;
    case 'buttonVariant':
      return typeof value === 'string' && BUTTON_VARIANTS.has(value) ? value : undefined;
    case 'borderRadius':
      return isFiniteNumber(value) && value >= 0 && value <= 9999 ? value : undefined;
    case 'borderWidth':
      return isFiniteNumber(value) && value >= 0 && value <= 100 ? value : undefined;
    case 'padding':
      return isFiniteNumber(value) && value >= 0 && value <= 9999 ? value : undefined;
    case 'opacity':
      return isFiniteNumber(value) && value >= 0 && value <= 1 ? value : undefined;
    case 'boxShadow':
      return typeof value === 'string' && Object.prototype.hasOwnProperty.call(SHADOW_PRESETS, value)
        ? value
        : undefined;
    case 'textColor':
    case 'backgroundColor':
    case 'borderColor':
      return isParseableColor(value) ? value : undefined;
    case 'fontFamily':
      return typeof value === 'string' && value.trim() !== '' ? value : undefined;
    default:
      return undefined;
  }
}

// ── Resolved-option helpers (override-or-default) ─────────────────────────────

/** Resolve a single coerced option for `item`, falling back to `fallback` when the
 *  override is absent/malformed/out-of-range. */
function option(item, name, fallback) {
  const st = (item && item.style) || {};
  const coerced = coerceStyleOption(name, st[name]);
  return coerced === undefined ? fallback : coerced;
}

/** The resolved box corner radius for `item` (override or per-type default). */
function boxRadius(item) {
  return option(item, 'borderRadius', boxDefaultsFor(item && item.type).borderRadius);
}

/** The resolved box padding (number) for `item` (override or per-type default). */
function boxPadding(item) {
  return option(item, 'padding', boxDefaultsFor(item && item.type).padding);
}

/** Optional URL/string used as an item background image for container/paper boxes. */
function boxBackgroundImage(item) {
  const st = (item && item.style) || {};
  const value = st.backgroundImage;
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

// ── Grid column derivation (Req 3.5, 7.3; Property 13) ────────────────────────

/**
 * The resolved Image_Grid column count for a `grid` Item (Req 3.5, 7.3; Property 13):
 * the explicit `gridColumns` override (validated to 1–12, then clamped to the single
 * shared `[1, MAX_GRID_COLS]`) when present, otherwise
 * `clamp(ceil(sqrt(cellCount)), 1, MAX_GRID_COLS)` where `cellCount` is the stored
 * `content.cellCount` or the cell array length. Total: never throws.
 *
 * @param {object} item  the grid Item
 * @returns {number} an integer column count in `[1, MAX_GRID_COLS]`
 */
export function gridColumns(item) {
  const explicit = coerceStyleOption('gridColumns', item && item.style && item.style.gridColumns);
  if (explicit !== undefined) return clamp(explicit, 1, MAX_GRID_COLS);

  const content = (item && item.content) || {};
  const cells = Array.isArray(content.cells) ? content.cells : [];
  const rawCount = isFiniteNumber(content.cellCount) ? content.cellCount : cells.length;
  const n = rawCount > 0 ? rawCount : 1;
  return clamp(Math.ceil(Math.sqrt(n)), 1, MAX_GRID_COLS);
}

// ── Box style (Req 3.1, 6, 7.5; Property 8) ───────────────────────────────────

/**
 * Resolve the box-wrapper style for ANY Item (Req 3.1, 6.1–6.3, 7.5; Property 8).
 * Returns a plain object whose every box property is DEFINED for the six known types
 * (`backgroundColor` is defined only where the type applies one — `text`/`paper`/
 * `container` — and is `undefined` for `image`/`grid`/`button`, matching ItemView).
 * Values come from valid Item_Style overrides, else the single per-type default in
 * `itemConstants.BOX_DEFAULTS`.
 *
 * The geometry/positioning, selection chrome, and `pointerEvents` from `ItemView` are
 * deliberately NOT part of this object — they are builder-only and live in the adapter
 * so they never affect parity (design D1 / Req 3.2). `boxShadow` is returned as the
 * resolved CSS string (`'none'` included) so the value is always defined.
 *
 * @param {object} item            the Item (any/malformed input tolerated)
 * @param {object} resolvedTheme   the cascade-resolved `{ textColor, backgroundColor, fontFamily }`
 * @returns {{ backgroundColor: (string|undefined), borderRadius: number, borderWidth: number,
 *            borderColor: string, boxShadow: string, padding: number, opacity: number }}
 */
export function resolveBoxStyle(item, resolvedTheme) {
  const type = item && item.type;
  const defaults = boxDefaultsFor(type);
  const rt = resolvedTheme || {};

  // backgroundColor mirrors ItemView's per-type computation exactly (Req 6.1, 6.2, 8.x):
  //   paper → resolved || '#ffffff'; container → resolved || 'transparent';
  //   text  → resolved (no fallback); every other type → undefined (no background).
  let backgroundColor;
  if (type === 'paper') backgroundColor = rt.backgroundColor || BACKGROUND_FALLBACK.paper;
  else if (type === 'container') backgroundColor = rt.backgroundColor || BACKGROUND_FALLBACK.container;
  else if (type === 'text') backgroundColor = rt.backgroundColor;
  else backgroundColor = undefined;

  // boxShadow: valid preset key override, else the per-type default key, → CSS string.
  const shadowKey = option(item, 'boxShadow', defaults.boxShadow);
  const boxShadow = SHADOW_PRESETS[shadowKey] || SHADOW_PRESETS.none;

  const isTransparentBg = backgroundColor === 'transparent';
  const defaultBorderWidth = isTransparentBg ? 0 : defaults.borderWidth;
  const defaultBorderColor = isTransparentBg ? 'transparent' : defaults.borderColor;

  return {
    backgroundColor,
    backgroundImage: boxBackgroundImage(item),
    borderRadius: option(item, 'borderRadius', defaults.borderRadius),
    borderWidth: option(item, 'borderWidth', defaultBorderWidth),
    borderColor: option(item, 'borderColor', defaultBorderColor),
    boxShadow,
    padding: boxPadding(item),
    opacity: option(item, 'opacity', defaults.opacity),
  };
}

// ── Inner content descriptor (Req 3.4, 7.1–7.4, 8, 15.3; Properties 9, 18) ─────

/** Build the image-cell descriptor for an `image`/grid cell: a real image when `src`
 *  is present, otherwise the shared placeholder descriptor (label 'Image'). */
function imageDescriptor(src, alt, objectFit, radius) {
  const hasSrc = typeof src === 'string' && src !== '';
  return {
    kind: 'image',
    props: {
      src: hasSrc ? src : '',
      alt: typeof alt === 'string' ? alt : '',
      objectFit,
      borderRadius: radius,
      isPlaceholder: !hasSrc,
      placeholderLabel: hasSrc ? undefined : 'Image',
    },
    children: [],
  };
}

/**
 * Resolve the abstract inner content for an Item as a serializable ContentDescriptor
 * `{ kind: 'text'|'image'|'grid'|'button'|'empty', props, children }` capturing inner
 * element ordering/nesting (Req 3.4, 7.1–7.4, 8.5, 15.3; Properties 9, 18). The two
 * React adapters map this descriptor to elements identically. An unknown `type` — and
 * `container`/`paper` (styled boxes with no inner content) — resolve to `{ kind:
 * 'empty' }`. Total: never throws.
 *
 * @param {object} item            the Item (any/malformed input tolerated)
 * @param {object} resolvedTheme   the cascade-resolved `{ textColor, backgroundColor, fontFamily }`
 * @returns {{ kind: string, props?: object, children?: Array }}
 */
export function resolveContent(item, resolvedTheme) {
  const type = item && item.type;
  const rt = resolvedTheme || {};
  const content = (item && item.content) || {};
  const radius = boxRadius(item);

  switch (type) {
    case 'text': {
      const d = CONTENT_DEFAULTS.text;
      return {
        kind: 'text',
        props: {
          text: typeof content.text === 'string' ? content.text : '',
          color: rt.textColor,
          fontFamily: rt.fontFamily,
          fontSize: option(item, 'fontSize', d.fontSize),
          fontWeight: option(item, 'fontWeight', d.fontWeight),
          textAlign: option(item, 'textAlign', d.textAlign),
          lineHeight: option(item, 'lineHeight', d.lineHeight),
        },
        children: [],
      };
    }
    case 'image': {
      const objectFit = option(item, 'objectFit', CONTENT_DEFAULTS.image.objectFit);
      return imageDescriptor(content.src, content.alt, objectFit, radius);
    }
    case 'grid': {
      const d = CONTENT_DEFAULTS.grid;
      const objectFit = option(item, 'objectFit', d.objectFit);
      const cells = Array.isArray(content.cells) ? content.cells : [];
      return {
        kind: 'grid',
        props: {
          columns: gridColumns(item),
          gap: option(item, 'gridGap', d.gridGap),
          objectFit,
        },
        children: cells.map((cell) =>
          imageDescriptor(cell && cell.src, cell && cell.alt, objectFit, radius),
        ),
      };
    }
    case 'button': {
      const d = CONTENT_DEFAULTS.button;
      const variant = option(item, 'buttonVariant', d.buttonVariant);
      // Button fill/foreground fallback transcribed from ItemView (Req 8.5):
      //   fill = resolved.backgroundColor || BUTTON_FALLBACK.fill
      //   fg   = resolved.textColor || (variant === 'filled' ? BUTTON_FALLBACK.filledFg : fill)
      const fill = rt.backgroundColor || BUTTON_FALLBACK.fill;
      const fg = rt.textColor || (variant === 'filled' ? BUTTON_FALLBACK.filledFg : fill);
      const variantStyle =
        variant === 'outline'
          ? { background: 'transparent', color: fg, border: `1px solid ${fill}` }
          : variant === 'text'
          ? { background: 'transparent', color: fg, border: 'none' }
          : { background: fill, color: fg, border: 'none' };
      const padding = boxPadding(item);
      return {
        kind: 'button',
        props: {
          label: typeof content.label === 'string' && content.label !== '' ? content.label : 'Button',
          variant,
          fontFamily: rt.fontFamily,
          fontSize: option(item, 'fontSize', d.fontSize),
          fontWeight: option(item, 'fontWeight', d.fontWeight),
          borderRadius: radius,
          padding: padding || d.paddingFallback,
          background: variantStyle.background,
          color: variantStyle.color,
          border: variantStyle.border,
        },
        children: [],
      };
    }
    case 'container':
    case 'paper':
      // Styled layout boxes: appearance comes from the box style; no inner content.
      return { kind: 'empty' };
    default:
      // Unknown type → box with no inner content (Req 15.3; Property 18).
      return { kind: 'empty' };
  }
}

// ── Full Render_Result (Req 3.4, 18.1; Property 9) ────────────────────────────

/**
 * Resolve geometry to the parity unit `{ x, y, width, height, z }`, using whole-number
 * fallbacks for any non-finite field (design Render_Result; Req 18.2). Present finite
 * values pass through unchanged (the Persistence_Fold already rounds to whole pixels).
 */
function resolveGeometry(item) {
  const g = (item && item.geometry) || {};
  return {
    x: isFiniteNumber(g.x) ? g.x : 0,
    y: isFiniteNumber(g.y) ? g.y : 0,
    width: isFiniteNumber(g.width) ? g.width : 0,
    height: isFiniteNumber(g.height) ? g.height : 0,
    z: isFiniteNumber(g.z) ? g.z : 1,
  };
}

/**
 * Strip non-parseable `textColor`/`backgroundColor` overrides from an Item's style so
 * the Theme_Cascade resolves them as if absent (Req 8.4). Returns the original `item`
 * untouched when nothing needs stripping; otherwise a shallow copy with a cleaned
 * `style` (never mutates the input).
 */
function withParseableColors(item) {
  const st = item && item.style;
  if (st == null || typeof st !== 'object') return item;
  let next = st;
  for (const key of ['textColor', 'backgroundColor']) {
    if (Object.prototype.hasOwnProperty.call(st, key) && !isParseableColor(st[key])) {
      if (next === st) next = { ...st };
      delete next[key];
    }
  }
  return next === st ? item : { ...item, style: next };
}

/**
 * The full Render_Result used by the Parity_Verifier and the two React adapters
 * (Req 3.4, 18.1; Property 9). Resolves the Theme_Cascade once (after dropping
 * non-parseable color overrides per Req 8.4), then returns `{ geometry, style, content }`
 * where `style` is the resolved box style and `content` is the inner ContentDescriptor.
 * Because the builder and reader both call this with the same inputs, their
 * Render_Results are deep-equal across all three dimensions. Total: never throws.
 *
 * @param {object} item            the Item to render
 * @param {object} section         the Section (carries Section_Theme)
 * @param {object} templateTheme   the Template_Theme base (may be absent/partial)
 * @returns {{ geometry: { x, y, width, height, z }, style: object, content: object }}
 */
export function renderResult(item, section, templateTheme) {
  const cleanItem = withParseableColors(item);
  const resolvedTheme = resolveItemStyle(cleanItem, section, templateTheme);
  return {
    geometry: resolveGeometry(item),
    style: resolveBoxStyle(cleanItem, resolvedTheme),
    content: resolveContent(cleanItem, resolvedTheme),
  };
}

/** The set of supported Item types (re-exported for adapters/verifier convenience). */
export { ITEM_TYPES };
